import { FC, useEffect, useRef } from 'react';
import { StyledAIButtonWrapper } from './styles';
import { Button } from '@kizen/kds/Button';
import { useTranslation } from 'react-i18next';
import { textAugmentation } from '@kizen/api/ml';
import { useSelector } from 'react-redux';
import { getChosenBusiness } from 'store/authentication/selectors';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

const MAX_DESCRIPTION_LENGTH = 500;

type AIDescriptionProps = {
  aiDescription: string;
  onChange: (value: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
};

export const AIDescription: FC<AIDescriptionProps> = ({
  aiDescription = '',
  onChange,
  loading,
  setLoading,
}) => {
  const { t } = useTranslation();
  const chosenBusiness = useSelector(getChosenBusiness);
  const abortController = useRef<AbortController | null>(null);

  const onSwitchToAIDescription = async () => {
    if (aiDescription.length > MAX_DESCRIPTION_LENGTH) {
      setLoading(true);
      try {
        abortController.current = new AbortController();
        const res = await textAugmentation(
          import.meta.env.VITE_API_BASE_PATH!,
          chosenBusiness.id,
          {
            task_type: 'summarize',
            messages: [{ order: 1, content: aiDescription, type: 'query' }],
            is_streaming: true,
          },
          { signal: abortController.current.signal }
        );

        let summarized = '';

        for await (const chunk of res) {
          if (abortController.current?.signal.aborted) {
            return;
          }
          summarized += chunk;
        }

        if (summarized.length > MAX_DESCRIPTION_LENGTH) {
          const words = summarized.split(/\s+/);
          summarized = '';

          for (let i = 0; i < words.length; i++) {
            if (summarized.length + words[i].length >= MAX_DESCRIPTION_LENGTH) {
              break;
            }
            summarized += words[i] + ' ';
          }

          summarized = summarized.trim() + '…';
        }

        onChange(summarized);
      } catch (error) {
        monitoringExceptionHelper(error, {
          extra: {
            aiDescription,
          },
        });
      } finally {
        setLoading(false);
      }
    } else {
      onChange(aiDescription || '');
    }
  };

  useEffect(() => {
    return () => {
      abortController.current?.abort();
    };
  }, []);

  return aiDescription ? (
    <StyledAIButtonWrapper>
      <Button
        leftIcon={loading ? 'action-loading' : 'text-ai'}
        leftIconSettings={{
          size: 'md',
          tooltipDescriptionOverride: t('Use AI description'),
        }}
        variant="text"
        color="secondary"
        onClick={onSwitchToAIDescription}
        type="button"
        disabled={loading}
      />
    </StyledAIButtonWrapper>
  ) : null;
};
